import { defineDocument } from '../../schema';
export const blogFreshOutOfTheOvenFinishEachOthersSandwichesDocument = defineDocument({
    id: 'blog:fresh-out-of-the-oven-finish-each-others-sandwiches',
    kind: 'blog',
    slug: 'fresh-out-of-the-oven-finish-each-others-sandwiches',
    route: '/blog/fresh-out-of-the-oven-finish-each-others-sandwiches',
    seo: {
        title: "Fresh Out of the Oven: Finish Each Other's Sandwiches",
        description: 'Our first cooperative team game returns with four rounds of mind-melded, spirit-pleasing sandwiches',
        imageAsset: 'coverFinishEachOthersSandwiches',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: "Fresh Out of the Oven: Finish Each Other's Sandwiches",
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Our first cooperative team game returns with four rounds of mind-melded, spirit-pleasing sandwiches',
            },
        ],
        imageAsset: 'coverFinishEachOthersSandwiches',
        imageAlt: "Finish Each Other's Sandwiches cover art",
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2025-10-26T00:00:00.000Z'),
        tags: ['games'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "There's something both satisfying and a little spooky about discovering you're on the exact same wavelength as someone else.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "That's the feeling we chased when we rebuilt ",
                        },
                        {
                            type: 'link',
                            href: '/discover/games/finish-each-others-sandwiches',
                            children: [
                                {
                                    type: 'text',
                                    text: "Finish Each Other's Sandwiches",
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ", our first fully collaborative team game. Today, we're excited to announce it's officially back, freshly baked and reimagined as a cooperative mind-melding feast where matching answers matter more than individual glory.",
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__four-rounds-one-shared-brain',
                    title: [
                        {
                            type: 'text',
                            text: 'Four Rounds, One Shared Brain',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'When we sat down to design this as our first team game, we knew we wanted to test whether players could think in sync across multiple dimensions. The result? Four distinct rounds, each with its own way of measuring how well your crew knows each other.',
                                },
                            ],
                        },
                        {
                            type: 'list',
                            items: [
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Quick Bites:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' Rapid-fire opinion questions with multiple choice answers. Do you see eye-to-eye, or are you about to discover your teammates have truly questionable taste?',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Snack Attack:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' Single-word associations to reveal how your minds make connections. See "blue" and think "ocean"? Your teammate might think "sad." Those mismatches won\'t earn you any points.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Same Order:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' Self-reflection statements where you predict what a designated player will choose. Can you get inside their head?',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'The Whole Enchilada:',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' Open-ended prompts where you complete the thought. This challenging round is a make-or-break moment for teams to put up a big score and bring home the bacon (lettuce and tomato).',
                                    },
                                ],
                            ],
                        },
                    ],
                },
                {
                    id: 'article__the-sandwich-spirits-are-watching',
                    title: [
                        {
                            type: 'text',
                            text: 'The Sandwich Spirits Are Watching',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Points scale with how many players match for every answer. The closer your minds align, the higher your crew climbs toward impressing the mysterious Sandwich Spirits. They are the picky judges who reward teamwork with increasingly impressive achievement levels.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'This is a cooperative game, so there are no individual winners. Whether your team is "Not Impressive" or achieves "Mouth-Wateringly Manifested" status, the achievement (or lack thereof) is yours to share.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__inspired-by-a-classic',
                    title: [
                        {
                            type: 'text',
                            text: 'Inspired by a Classic',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'One of our greatest inspirations for this game is ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Hanabi',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', a cooperative card game where players work together while individually strategizing how to help the team. You can see that influence in the achievement levels and how each player must think independently, yet success only comes when the whole crew aligns.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "What makes this different is we're not testing memory or strategy. We're testing whether you actually know how your teammates think. It's a game that reveals whether you're all operating on different frequencies or truly have that special bond.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__from-beta-to-beautiful',
                    title: [
                        {
                            type: 'text',
                            text: 'From Beta to Beautiful',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Finish Each Other's Sandwiches actually started as the very first game on our beta platform, but it looked much different. The initial version wasn't collaborative and lacked the spark that makes a game feel truly special.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Working with our ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://discord.gg/rfFTVAqHwH',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Discord',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' community, especially with valuable input from streamers like ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://twitch.tv/waterdance',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Waterdance',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ", we completely reworked the formula. We leaned more heavily into both the psychic and sandwich aspects of the game. We kept many of the same functional mechanics, but added a lot of tasty twists to make this re-release truly feel original. For the OG's, The Whole Enchilada round will feel closest to the original beta version, while the other three rounds bring something brand new to the table.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__the-gradient-details',
                    title: [
                        {
                            type: 'text',
                            text: 'The Gradient Details',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "There's a subtle visual detail we're proud of, even though most players won't consciously notice it. When answers match in rounds 1 and 3 (Quick Bites and Same Order), the interface creates a gradient blending the colors of all the players who matched. In rounds 2 and 4 (Snack Attack and The Whole Enchilada), it draws a gradient line connecting the matching answers.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "It's a small touch, but it visually reinforces the feeling of minds melding together. Seeing your colors blend with your teammates' is satisfying when you're all thinking the same way.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__building-better-teams',
                    title: [
                        {
                            type: 'text',
                            text: 'Building Better Teams',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "This isn't just a party game. Team leaders might consider opening a meeting with a quick round and then transitioning into an honest discussion about teamwork. The game naturally reveals where teams are aligned and where they're not.",
                                },
                            ],
                        },
                        {
                            type: 'quote',
                            content: [
                                {
                                    type: 'text',
                                    text: "A lot of team breakdowns happen when we try to read each other's minds or assume each other's intent. The best teams communicate well!",
                                },
                            ],
                            attribution: [
                                {
                                    type: 'text',
                                    text: 'Senior Product Manager at Webflow',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Did your team hit 'Mouth-Wateringly Manifested' on the first try? You might actually be psychic. Did you crash and burn in 'Not Impressive' territory? Maybe it's time to talk about how you communicate. Regardless of your final score, Finish Each Other's Sandwiches is a great way to start a conversation about teamwork.",
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__extra-sides-in-this-release',
                    title: [
                        {
                            type: 'text',
                            text: 'Extra Sides in This Release',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "We're serving up more than just sandwiches with this update:",
                                },
                            ],
                        },
                        {
                            type: 'list',
                            items: [
                                [
                                    {
                                        type: 'text',
                                        text: 'Upgraded generated options for ',
                                    },
                                    {
                                        type: 'link',
                                        href: '/discover/games/ego-maniacs',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Ego Maniacs',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' awards, ',
                                    },
                                    {
                                        type: 'link',
                                        href: '/discover/games/meme-mash',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Meme Mash',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' templates, and ',
                                    },
                                    {
                                        type: 'link',
                                        href: '/discover/games/wtf-is-happening',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'WTF Is Happening',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' newspaper sections.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'Improved vote handling and support for large games.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'Fixed a sound effect bug in ',
                                    },
                                    {
                                        type: 'link',
                                        href: '/discover/games/ego-maniacs',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Ego Maniacs',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: '.',
                                    },
                                ],
                            ],
                        },
                    ],
                },
                {
                    id: 'article__ready-to-test-your-team-sync',
                    title: [
                        {
                            type: 'text',
                            text: 'Ready to Test Your Team Sync?',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "If you're hungry to do some mind-melding, ",
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "start a game of Finish Each Other's Sandwiches now.",
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Did you discover your team is full of psychics? Did you win the approval of the Sandwich Spirits? Share your results in ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://discord.gg/rfFTVAqHwH',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Pals Discord",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=fresh-out-of-the-oven-finish-each-others-sandwiches.js.map